import { useCallback } from 'react'

import { env } from 'src/config/env'
import { useEventStore } from 'src/shared/store/event-store'
import { sendGoogleAdsConversion } from 'src/shared/utils/google-ads-conversion'

// Define conversion actions as an enum
export enum ConversionAction {
  // Real estate form submissions
  SUBMIT_REAL_ESTATE_LEAD_FORM = 'SUBMIT_REAL_ESTATE_LEAD_FORM',
  // Loan officer form submissions
  SUBMIT_LOAN_OFFICER_LEAD_FORM = 'SUBMIT_LOAN_OFFICER_LEAD_FORM',
  // Call requests
  REQUEST_CALL = 'REQUEST_CALL',
  // Start journey button pressed. Mobile sms or web phone number submission.
  START_JOURNEY_BTN = 'START_JOURNEY_BTN',
}

// Map conversion actions to their respective Google Ads conversion labels
const CONVERSION_LABELS: Record<ConversionAction, string> = {
  [ConversionAction.SUBMIT_REAL_ESTATE_LEAD_FORM]:
    env.MODE === 'prod' ? 'Fx1LCJC4ssAaEMP3mqo-' : '1aLhCIKywsAaEMP3mqo-',
  [ConversionAction.SUBMIT_LOAN_OFFICER_LEAD_FORM]:
    env.MODE === 'prod' ? 'UCR2CI24ssAaEMP3mqo-' : '3k4iCP-xwsAaEMP3mqo-',
  [ConversionAction.REQUEST_CALL]: env.MODE === 'prod' ? 'RgVxCNSrssAaEMP3mqo-' : 'MBsKCPyxwsAaEMP3mqo-',
  [ConversionAction.START_JOURNEY_BTN]: env.MODE === 'prod' ? 'eHoFCOm56OwaEMP3mqo-' : '',
}

/**
 * Hook for tracking Google Ads conversions
 * Uses GCLID from event store if available
 */
export const useConversionTracking = () => {
  const { gclid } = useEventStore()

  /**
   * Track a conversion event with optional URL redirect
   * @param action - The conversion action to track
   * @param redirectUrl - Optional URL to redirect to after conversion (following Google's pattern)
   * @param value - Optional conversion value
   */
  const trackConversion = useCallback(
    async (action: ConversionAction, value?: number) => {
      const conversionLabel = CONVERSION_LABELS[action]
      // Use environment variable if available, otherwise use the default
      const conversionId = env.GOOGLE_ADS_CONVERSION_ID

      if (!conversionLabel || !conversionId) {
        console.error(`No conversion label or conversion ID found for action: ${action}`)
        return
      }

      await sendGoogleAdsConversion(conversionId, conversionLabel, gclid, value)
    },
    [gclid],
  )

  return { trackConversion }
}
